import { addHours, isAfter } from 'date-fns';
import { setExtendedImageDimensionsEnabled } from '@bynder-studio/render-core';
import groupsService from 'packages/services/groupsService';
import fontsService from 'packages/services/fontsService';
import trackingService from 'packages/services/trackingService';
import userService from 'packages/services/userService';
import authService from 'packages/services/authService';
import featureTreatmentService from 'packages/services/featureTreatmentService';
import filtersStorage from 'packages/hooks/filtersStorage';
import { catchResponseError } from 'packages/helpers/helpers';
import { parseUrl } from 'packages/helpers/urlUtils';
import AuthorizationHelper from 'packages/helpers/AuthorizationHelper';
import isInIframe from 'packages/helpers/iframeHelper';
import { stripBasePath } from 'packages/helpers/navigation';
import ProxyServerConnector from 'packages/services/connectors/ProxyServerConnector';
import { findDefaultFontId, toFontFamilies } from 'packages/services/fontsAdapter';
import { updateEditorFonts } from 'packages/store/creativeEditor/creativeEditor.actions';
import * as types from 'packages/store/types';
import { ActiveSplits } from './types';

const { syncForUser, getFiltersFromStorage, setFiltersToStorage } = filtersStorage();

let refetchFontsIntervalId: any = -1;
let visibilityChangeHandler: any = null;

export function getUser() {
    return async (dispatch) => {
        try {
            const { status, json: data } = (await userService.getUser()) as any;

            if (status === 200 && data) {
                AuthorizationHelper.setPermissions(data.permissions, data.company);
                AuthorizationHelper.setRoles(data.roles);
                AuthorizationHelper.setFeatures(data.accessibleFeatures);

                const currentCompanyData = data.companies.find((company) => company.id === data.company);
                AuthorizationHelper.appcuesIdentify(data.user, currentCompanyData, data.roles, data.accessibleFeatures);
                syncForUser(data.user.bynderUserId || data.user.id);

                dispatch(getUserFonts());

                dispatch({
                    type: types.LOGIN_SUCCESS,
                    user: data.user,
                    companies: data.companies,
                    currentCompany: data.company,
                    permissions: data.permissions,
                    roles: data.roles,
                    accessibleFeatures: data.accessibleFeatures,
                    bynderScopes: data.bynderScopes,
                });

                dispatch({
                    type: types.TOGGLE_APP_INITIALIZING,
                    state: false,
                });

                const storedUser = getFiltersFromStorage('loginTracking');

                if (storedUser) {
                    if (isAfter(new Date(), addHours(new Date(storedUser.currentDateTime), 1))) {
                        await trackingService.registerLogin({
                            bynderUserId: data.user.bynderUserId,
                            bynderAccountId: data.companies[0].bynderAccountId,
                        });

                        setFiltersToStorage('loginTracking', {
                            bynderUserId: data.user.bynderUserId,
                            bynderAccountId: data.companies[0].bynderAccountId,
                            currentDateTime: new Date(),
                        } as any);
                    }
                } else {
                    await trackingService.registerLogin({
                        bynderUserId: data.user.bynderUserId,
                        bynderAccountId: data.companies[0].bynderAccountId,
                    });

                    setFiltersToStorage('loginTracking', {
                        bynderUserId: data.user.bynderUserId,
                        bynderAccountId: data.companies[0].bynderAccountId,
                        currentDateTime: new Date(),
                    } as any);
                }

                dispatch(getGroups());
            }
        } catch (err) {
            catchResponseError(err);
        }
    };
}

function redirectToBynderLogin(loginUrl: string) {
    return (dispatch) => {
        if (location.pathname !== '/bynder-auth') {
            const redirectUrl = `${location.pathname}${location.search}`;
            console.warn('Set url on', redirectUrl);
            sessionStorage.setItem('redirectUrl', redirectUrl);
        }

        if (isInIframe()) {
            dispatch(sendRedirectCommand(loginUrl));
        } else {
            window.location.href = loginUrl;
        }
    };
}

export function loginBynderUser() {
    return async (dispatch, getState) => {
        const { portalDomain, bynderAccessToken } = getState().platform;

        if (!bynderAccessToken) {
            console.error('loginBynderUser: No bynder access token.');

            return;
        }

        const finishLogin = () => {
            dispatch(getUser());
            ProxyServerConnector.flushWaitList();
        };

        // TEMPORARY: When the Kong gateway is enabled it handles auth directly, so we
        // skip the identity-token exchange (internal-api to kong-api migration).
        // TODO(kong-migration): remove this branch once the migration is complete.
        const [kongTreatment] = await Promise.all([
            dispatch(fetchKongGatewayFeatureTreatment()),
            dispatch(fetchImageMaxDimensionsFeatureTreatment()),
        ]);

        // Not awaited: the treatment only affects rendering, so it must not delay login.
        dispatch(fetchFeatureTreatment(ActiveSplits.studioDsColorPicker));
        dispatch(fetchFeatureTreatment(ActiveSplits.studioTextOutlineColor));
        dispatch(fetchFeatureTreatment(ActiveSplits.studioSelfFontManagement));
        dispatch(fetchFeatureTreatment(ActiveSplits.imageGenAsyncProcessing));

        if (kongTreatment === 'on') {
            finishLogin();

            return;
        }

        try {
            const authResponse = await authService.exchangeBynderToIdentityToken({
                portalDomain,
                accessToken: bynderAccessToken,
            });

            if (authResponse.status !== 200) {
                return;
            }

            if (authResponse.json.loginUrl) {
                const currentPath = stripBasePath(window.location.pathname);

                if (currentPath !== '/bynder-auth') {
                    const redirectUrl = `${currentPath}${window.location.search}`;
                    console.warn('Set url on', redirectUrl);
                    sessionStorage.setItem('redirectUrl', redirectUrl);
                }

                if (isInIframe()) {
                    dispatch(sendRedirectCommand(authResponse.json.loginUrl));
                } else {
                    window.location.href = authResponse.json.loginUrl;
                }

                return;
            }

            dispatch({
                type: types.UPDATE_PLATFORM_OPTIONS,
                options: {
                    accessToken: authResponse.json.identityAccessToken,
                },
            });

            finishLogin();
        } catch (err) {
            catchResponseError(err);
        }
    };
}

export function registerBynderUser(authCode: string, state: string, cb?: () => void) {
    return async (dispatch) => {
        dispatch({
            type: types.UPDATE_PLATFORM_OPTIONS,
            options: {
                bynderAccessToken: null,
            },
        });

        try {
            const {
                json: { bynderAccessToken },
                status,
            } = (await authService.registerBynderUser({ authCode, state })) as any;

            if (status === 200) {
                dispatch({
                    type: types.UPDATE_PLATFORM_OPTIONS,
                    options: {
                        bynderAccessToken,
                    },
                });

                if (cb) {
                    cb();
                }
            }
        } catch (err) {
            catchResponseError(err);
        }
    };
}

function getGroups() {
    return (dispatch) => {
        dispatch({ type: types.GET_GROUPS_REQUEST });
        groupsService
            .getGroups()
            .then(({ json }: any) => {
                dispatch({ type: types.GET_GROUPS_SUCCESS, groups: json.items });
            })
            .catch(catchResponseError);
    };
}

export function getUserFonts(): any {
    return (dispatch) => {
        if (visibilityChangeHandler) {
            document.removeEventListener('visibilitychange', visibilityChangeHandler);
        }

        clearInterval(refetchFontsIntervalId);

        dispatch({ type: types.GET_FONTS_REQUEST });

        const updateFonts = () =>
            fontsService
                .getFonts()
                .then(({ status, json }) => {
                    if (status >= 400 || !Array.isArray(json)) {
                        return;
                    }

                    dispatch({
                        type: types.GET_FONTS_SUCCESS,
                        fonts: toFontFamilies(json),
                        defaultFontId: findDefaultFontId(json),
                    });
                    dispatch(updateEditorFonts());
                })
                .catch(catchResponseError);

        const initialLoad = updateFonts();
        refetchFontsIntervalId = setInterval(updateFonts, 1000 * 60 * 10);

        // save callback in the upper scope to be able to remove when the function is called again
        visibilityChangeHandler = () => {
            if (document.hidden) {
                // Clear interval when tab becomes inactive
                clearInterval(refetchFontsIntervalId);
            } else {
                // Refetch fonts and create a new interval when tab becomes active
                updateFonts();
                refetchFontsIntervalId = setInterval(updateFonts, 1000 * 60 * 10);
            }
        };

        document.addEventListener('visibilitychange', visibilityChangeHandler);

        return initialLoad;
    };
}

export function logout(): any {
    return (dispatch, getState) => {
        const { hostPort } = getState().platform;

        if (hostPort) {
            hostPort.postMessage({
                type: 'logout',
            });
        } else {
            // Embedded directly in the host's page: it owns navigation, so ask
            // it to log out rather than tearing its page down with a redirect.
            window.dispatchEvent(new CustomEvent('vbs-console:logout'));
        }
        // clearAllFiltersFromStorage();

        dispatch({ type: types.LOGOUT });
    };
}

/**
 * Iframe-only: asks the host for a fresh token over the message channel. Other
 * deployments go through `refreshAccessToken` in platform.actions instead.
 */
export function sendRefreshCommand(): any {
    return (dispatch, getState) => {
        getState().platform.hostPort?.postMessage({
            type: 'refresh_token',
        });
        setFiltersToStorage('loginTracking', {
            ...getFiltersFromStorage('loginTracking'),
            currentDateTime: new Date(),
        });
    };
}

function sendRedirectCommand(url) {
    return (dispatch, getState) => {
        getState().platform.hostPort.postMessage({
            type: 'redirect',
            url,
        });
    };
}

/**
 * Fetches a single Split treatment and stores it under `user.splitFeatures`.
 * Failures are swallowed so a flag lookup can never block the caller.
 */
export function fetchFeatureTreatment(featureName) {
    return async (dispatch, getState) => {
        const { portalDomain, bynderAccessToken } = getState().platform;

        if (!bynderAccessToken) {
            console.error('fetchFeatureTreatment: No bynder access token.');

            return;
        }

        try {
            const treatment = await featureTreatmentService.fetchTreatment(
                portalDomain,
                bynderAccessToken,
                featureName,
            );

            dispatch({ type: types.SET_SPLIT_FEATURES, featureName, featureValue: treatment });

            return treatment;
        } catch (error) {
            console.error('fetchFeatureTreatment: Failed to fetch treatment for', featureName, error);
        }
    };
}

/**
 * TEMPORARY: This function is used for testing the migration from internal-api to kong-api.
 * It fetches the 'studio_kong_gateway' feature flag to determine whether to route
 * requests through the Kong gateway. This should be removed once the migration is complete.
 */
export function fetchKongGatewayFeatureTreatment() {
    return async (dispatch, getState) => {
        const treatment = await dispatch(fetchFeatureTreatment(ActiveSplits.studioKongGateway));

        if (treatment === 'on') {
            const { portalDomain } = getState().platform;

            ProxyServerConnector.setGatewayPrefix(`${parseUrl(portalDomain)}/v7/studio`);
        }

        return treatment;
    };
}

export function fetchImageMaxDimensionsFeatureTreatment() {
    return async (dispatch) => {
        const treatment = await dispatch(fetchFeatureTreatment(ActiveSplits.studioExtendedImageDimensions));

        setExtendedImageDimensionsEnabled(treatment === 'on');

        return treatment;
    };
}
